import React, { useCallback } from "react";
import { View, StyleSheet } from "react-native";
import {
  Cigarette,
  CircleOff,
  Leaf,
  Plus,
  type LucideIcon,
  Wine,
  Wind,
} from "lucide-react-native";
import { useNavigation } from "@react-navigation/native";

import { SquareOptionButton } from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useQueuedPatientUpdate } from "@src/packages/patients/sync/usePatientSync";
import { BasicInfoQuestionLayout } from "../components";
import { useBasicInfo } from "../BasicInfoContext";
import {
  buildQuickExposureLifestylePatch,
  QUICK_EXPOSURE_SUBSTANCES,
} from "../quickExposure";
import type { QuickExposureSubstance } from "../types";

const QUICK_EXPOSURE_ICONS: Record<QuickExposureSubstance, LucideIcon> = {
  alcohol: Wine,
  cannabis: Leaf,
  cigarettes: Cigarette,
  vape: Wind,
  other: Plus,
};

const QUICK_EXPOSURE_LABEL_KEYS: Record<QuickExposureSubstance, string> = {
  alcohol: "med_history.basic_info.quick_exposure.alcohol",
  cannabis: "med_history.basic_info.quick_exposure.cannabis",
  cigarettes: "med_history.basic_info.quick_exposure.cigarettes",
  vape: "med_history.basic_info.quick_exposure.vape",
  other: "med_history.basic_info.quick_exposure.other",
};

export function QuickExposureScreen() {
  const t = useTranslations();
  const navigation = useNavigation();
  const { state, updateState, next } = useBasicInfo("QuickExposure");
  const { data: patient } = usePatient();
  const { mutateAsync: updatePatient, isPending } = useQueuedPatientUpdate();

  const handleToggle = useCallback(
    async (substance: QuickExposureSubstance | "none") => {
      const current = state.quickExposureSubstances;
      const selected = new Set(current ?? []);

      if (substance === "none") {
        selected.clear();
      } else if (selected.has(substance)) {
        selected.delete(substance);
      } else {
        selected.add(substance);
      }

      const nextSelection = QUICK_EXPOSURE_SUBSTANCES.filter((option) =>
        selected.has(option),
      );

      updateState({ quickExposureSubstances: nextSelection });
      await updatePatient({
        lifestyle: buildQuickExposureLifestylePatch(patient, nextSelection),
      });
    },
    [patient, state.quickExposureSubstances, updatePatient, updateState],
  );

  const selected = state.quickExposureSubstances;
  const answered = selected != null;
  const noneSelected = selected != null && selected.length === 0;

  return (
    <BasicInfoQuestionLayout
      route="QuickExposure"
      title={t("med_history.basic_info.quick_exposure.title")}
      subtitle={t("med_history.basic_info.quick_exposure.subtitle")}
      helpText={t("med_history.basic_info.help.quick_exposure")}
      onBack={() => navigation.goBack()}
      onSave={() => next()}
      saveDisabled={!answered || isPending}
      saveLabel={t("med_history.shared.next")}
    >
      <View style={styles.optionsGrid}>
        {QUICK_EXPOSURE_SUBSTANCES.map((substance) => {
          const Icon = QUICK_EXPOSURE_ICONS[substance];
          return (
            <View key={substance} style={styles.optionWrapper}>
              <SquareOptionButton
                icon={Icon}
                label={t(QUICK_EXPOSURE_LABEL_KEYS[substance])}
                isSelected={selected?.includes(substance) ?? false}
                onPress={() => handleToggle(substance)}
                iconSize={22}
                labelSize="sm"
              />
            </View>
          );
        })}
        <View style={styles.optionWrapper}>
          <SquareOptionButton
            icon={CircleOff}
            label={t("med_history.basic_info.quick_exposure.none")}
            isSelected={noneSelected}
            onPress={() => handleToggle("none")}
            iconSize={22}
            labelSize="sm"
          />
        </View>
      </View>
    </BasicInfoQuestionLayout>
  );
}

const styles = StyleSheet.create({
  optionsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 40,
    width: "100%",
  },
  optionWrapper: {
    width: "47%",
    minWidth: "47%",
  },
});

export default QuickExposureScreen;
